import {
  BoundCheckboxField,
  BoundNumberField,
  BoundTextAreaField,
  BoundTextField,
  Button,
  Checkbox,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  PageHeaderLayout,
  SelectField,
  simpleHeader,
  Tag,
  useComputed,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { NavigationBlocker } from "src/components/navigation/NavigationBlocker";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  DesignPackageLocation,
  FeatureFlagType,
  FinishScheduleColumnType,
  SaveCostCodeInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  FinishScheduleCostCodeDetailFragment,
  useFinishScheduleCostCodeQuery,
  useSaveFinishScheduleCostCodeMutation,
} from "src/routes/admin/finishSchedule/FinishScheduleCostCodeDetails.generated";
import { reorderRows } from "src/routes/libraries/design-catalog/personalization-option/utils";
import { adminFinishScheduleRoute, adminRoute } from "src/routing/domains/adminPaths";
import { noop, queryResult } from "src/utils";
import { ConfirmLeaveChangesModal } from "./ConfirmLeaveChangesModal";

export function FinishScheduleCostCodeDetailsPage() {
  const isEnabled = useFeatureFlag(FeatureFlagType.FinishSchedulesV2);
  const { costCodeId } = useRouteParams<{ costCodeId: string }>();
  const [searchParams] = useSearchParams();
  const query = useFinishScheduleCostCodeQuery({ variables: { id: costCodeId }, skip: !isEnabled });

  if (!isEnabled) return <Navigate to={adminRoute.url()} replace />;

  return queryResult(query, {
    data: ({ costCodes }) => {
      const costCode = costCodes.first;
      if (!costCode) return <div css={Css.sm.gray700.$}>Cost code not found.</div>;
      const location = resolveLocation(searchParams.get("location"), costCode);
      return <FinishScheduleCostCodeView costCode={costCode} location={location} />;
    },
  });
}

/**
 * The section to edit. Honors an explicit `?location=` (the tab that linked here); with no/invalid
 * param (direct link or bookmark), defaults to whichever section the cost code has so an exterior-only
 * cost code doesn't open an empty Interior view.
 */
function resolveLocation(param: string | null, costCode: FinishScheduleCostCodeDetailFragment): DesignPackageLocation {
  if (param === DesignPackageLocation.Interior) return DesignPackageLocation.Interior;
  if (param === DesignPackageLocation.Exterior) return DesignPackageLocation.Exterior;
  return costCode.interiorFinishScheduleConfigs.length
    ? DesignPackageLocation.Interior
    : DesignPackageLocation.Exterior;
}

function FinishScheduleCostCodeView(props: {
  costCode: FinishScheduleCostCodeDetailFragment;
  location: DesignPackageLocation;
}) {
  const { costCode, location } = props;
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const [saveCostCode] = useSaveFinishScheduleCostCodeMutation();

  const formState = useFormState({
    config: formConfig,
    init: { input: costCode, map: (cc) => mapToForm(cc, location) },
  });

  async function onSave() {
    if (!formState.canSave()) return;
    const { finishScheduleConfigs, ...rest } = formState.changedValue;
    const costCodeInput = {
      ...rest,
      ...(finishScheduleConfigs && {
        finishScheduleConfigs: finishScheduleConfigs.map(({ type, coreColumnName, ...config }) => config),
      }),
    };
    const { data } = await saveCostCode({ variables: { input: { costCodes: [costCodeInput] } } });
    // Re-baseline the form from the server response (runs init.map) so it resets to clean explicitly,
    // rather than depending on the Apollo cache refresh to rerender useFormState.
    const saved = data?.saveCostCodes.costCodes.find((cc) => cc.id === costCode.id);
    if (saved) formState.update(saved);
    triggerNotice({ message: "Section saved", icon: "success" });
  }

  // This page edits one section (interior or exterior); pick that side's label/description fields.
  const isExterior = location === DesignPackageLocation.Exterior;
  const labelField = isExterior ? formState.exteriorFinishScheduleLabel : formState.interiorFinishScheduleLabel;
  const descriptionField = isExterior
    ? formState.exteriorFinishScheduleDescription
    : formState.interiorFinishScheduleDescription;
  const customLabel = isExterior ? costCode.exteriorFinishScheduleLabel : costCode.interiorFinishScheduleLabel;
  const sectionName = customLabel ?? costCode.name;

  const [viewItemId, setViewItemId] = useState<string | undefined>(undefined);

  // Items with material attributes drive the attribute columns.
  const materialItems = useMemo(
    () => costCode.items.filter((i) => i.materialAttributeDimensions.length > 0),
    [costCode.items],
  );

  // `indexBy` (not `groupBy`) because it's one-to-many: an item lands under every attribute it uses.
  const itemsByAttribute = useMemo(
    () => materialItems.indexBy((item) => item.materialAttributeDimensions.map((mad) => mad.name)),
    [materialItems],
  );

  // Custom config items for this section (e.g. Tile's Lay Pattern / Grout / Trim, Cabinet Hardware),
  // rendered as read-only columns pinned to the bottom.
  const childItems = useMemo(
    () => costCode.finishScheduleChildItems.filter((i) => i.designPackageLocation.code === location),
    [costCode.finishScheduleChildItems, location],
  );

  const attributeColumns = useMemo(
    () => createAttributeColumns(formState, itemsByAttribute),
    [formState, itemsByAttribute],
  );

  const isDirty = useComputed(() => formState.dirty, [formState]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: `Edit Section '${sectionName}'`,
        breadcrumbs: {
          breadcrumbs: { href: adminFinishScheduleRoute.url(), label: "Admin / Finish Schedule Sections" },
        },
        rightSlot: (
          <>
            <Button label="Cancel" variant="tertiary" onClick={() => navigate(adminFinishScheduleRoute.url())} />
            <Button label="Save" onClick={onSave} />
          </>
        ),
      }}
    >
      <div css={Css.df.fdc.gap4.mxa.wPx(720).maxw100.px3.pb4.$}>
        <div>
          <h2 css={Css.lg.mt6.$}>{sectionName}</h2>
          <p css={Css.sm.gray700.mt4.$}>
            These settings control the columns that appear for products within the "{sectionName}" cost code group.
            Show/hide, reorder, and rename as desired.
          </p>
        </div>
        <BoundTextField label="Section Name" field={labelField} />
        <BoundTextAreaField label="Description" field={descriptionField} />
        <div>
          <h3 css={Css.lg.$}>Item Attributes</h3>
          <p css={Css.sm.gray700.mt1.mb2.$}>
            Controls the column order in the export. Attributes used by multiple items appear only once. All items will
            share the same column.
          </p>
          <div css={Css.mb2.wPx(280).$}>
            <SelectField
              data-testid="viewItem"
              label="View"
              value={viewItemId}
              onSelect={setViewItemId}
              options={materialItems}
              unsetLabel="All Items"
            />
          </div>
          <Observer>
            {() => {
              const viewItem = materialItems.find((i) => i.id === viewItemId);
              return (
                <GridTable
                  id="attributesTable"
                  rows={createAttributeRows(formState, viewItem, childItems)}
                  columns={attributeColumns}
                  style={{ bordered: true, allWhite: true }}
                />
              );
            }}
          </Observer>
        </div>
      </div>
      <NavigationBlocker when={() => isDirty}>
        {({ onConfirm, onCancel }) => (
          <ConfirmLeaveChangesModal
            onConfirm={onConfirm}
            onCancel={onCancel}
            revertAll={() => formState.revertChanges()}
          />
        )}
      </NavigationBlocker>
    </PageHeaderLayout>
  );
}

type FinishScheduleItem = FinishScheduleCostCodeDetailFragment["items"][number];

// ---- Item Attributes table (editable finishScheduleConfigs) ----

type AttributeRow =
  | { kind: "header"; data: undefined }
  | { kind: "config"; id: string; data: ObjectState<FinishScheduleConfigForm> }
  // Custom config items (e.g. Cabinet Hardware) as read-only columns; not form-backed, never saved.
  | { kind: "childItem"; id: string; data: { name: string } };

function createAttributeColumns(
  formState: ObjectState<CostCodeForm>,
  itemsByAttribute: Map<string, FinishScheduleItem[]>,
): GridColumn<AttributeRow>[] {
  return [
    column<AttributeRow>({
      header: "Order",
      config: (os) =>
        os.shown.value ? (
          <BoundNumberField
            compact
            labelStyle="hidden"
            label="Order"
            field={os.sortOrder}
            xss={Css.tal.$}
            // On edit, move this column to the typed position and renumber the rest to a contiguous 1..n.
            onBlur={() => renumberConfigOrders(formState, os.id.value!)}
          />
        ) : (
          <></>
        ),
      childItem: () => <Icon icon="lockOpen" inc={2} tooltip="This item is always included and can't be reordered." />,
      w: "80px",
    }),
    column<AttributeRow>({
      header: "Column Label",
      config: (os) => (
        <BoundTextField compact labelStyle="hidden" label="Column Label" field={os.label} readOnly={!os.shown.value} />
      ),
      childItem: ({ name }) => name,
      w: 2,
    }),
    column<AttributeRow>({
      header: "Source Attribute",
      config: (os) => os.sourceAttribute.value ?? os.coreColumnName.value ?? <></>,
      childItem: emptyCell,
      w: 2,
    }),
    column<AttributeRow>({
      header: "Used By",
      config: (os) => {
        const sourceAttribute = os.sourceAttribute.value;
        if (!sourceAttribute) return <Tag text="All" />;
        const items = itemsByAttribute.get(sourceAttribute) ?? [];
        return (
          <div css={Css.df.gap1.aic.add("flexWrap", "wrap").$}>
            {items.map((item) => (
              <Tag key={item.id} text={item.name} />
            ))}
          </div>
        );
      },
      childItem: emptyCell,
      w: 3,
    }),
    column<AttributeRow>({
      header: "Show",
      config: (os) => (
        <BoundCheckboxField
          label="Show"
          field={os.shown}
          // Showing a column appends it to the bottom of the order; hiding clears its number. Either
          // way we renumber the shown columns to a contiguous 1..n (core columns stay first).
          onChange={(shown) => {
            os.shown.set(shown);
            renumberShownConfigs(formState);
          }}
        />
      ),
      childItem: () => <Checkbox label="Show" selected disabled onChange={noop} />,
      w: "80px",
      align: "center",
    }),
    column<AttributeRow>({
      header: "Use in Visual Mode",
      config: (os) => <BoundCheckboxField label="Use in Visual Mode" field={os.useInVisualMode} />,
      childItem: () => <Checkbox label="Use in Visual Mode" selected disabled onChange={noop} />,
      w: "180px",
      align: "center",
    }),
  ];
}

type ConfigRow = ObjectState<FinishScheduleConfigForm>;

/** Shown columns first (by their current order), then hidden columns — the canonical display order. */
function shownFirstByOrder(rows: readonly ConfigRow[]): ConfigRow[] {
  return [...rows].sortBy((r) => [r.shown.value ? 0 : 1, r.sortOrder.value ?? Number.MAX_SAFE_INTEGER]);
}

/**
 * After an Order input is edited, move that config to its typed position and renumber the rest to a
 * contiguous 1..n (no duplicates/gaps) — mirrors the personalization-option wizard's reorder behavior.
 */
function renumberConfigOrders(formState: ObjectState<CostCodeForm>, editedId: string) {
  const rows = formState.finishScheduleConfigs.rows;
  const reordered = reorderRows(
    rows.map((r) => ({ id: r.id.value!, order: r.sortOrder.value })),
    editedId,
  ).keyBy((r) => r.id);
  // Keep the existing order if a row wasn't renumbered — the backend requires a non-null sortOrder.
  rows.forEach((row) => row.sortOrder.set(reordered[row.id.value!]?.order ?? row.sortOrder.value));
}

/**
 * Renumber after a Show toggle so shown configs stay a contiguous 1..n (core columns keep their lead;
 * a just-shown column, with no order yet, lands at the bottom). Hidden configs take the numbers after
 * the shown ones — the backend requires a non-null sortOrder, and the Order column is blank when hidden.
 */
function renumberShownConfigs(formState: ObjectState<CostCodeForm>) {
  shownFirstByOrder(formState.finishScheduleConfigs.rows).forEach((row, i) => row.sortOrder.set(i + 1));
}

function createAttributeRows(
  formState: ObjectState<CostCodeForm>,
  viewItem: FinishScheduleItem | undefined,
  childItems: { id: string; name: string }[],
): GridDataRow<AttributeRow>[] {
  // "View" filter: with an item selected, show only that item's attribute columns (core columns and
  // other items' attributes are hidden); with no item selected, show everything.
  const itemAttributes = new Set(viewItem?.materialAttributeDimensions.map((mad) => mad.name));
  const visible = formState.finishScheduleConfigs.rows.filter(
    (os) => !viewItem || (!!os.sourceAttribute.value && itemAttributes.has(os.sourceAttribute.value)),
  );
  const rows = shownFirstByOrder(visible).map((os) => ({ kind: "config" as const, id: os.id.value!, data: os }));
  // Custom config columns pinned after editable configs; hidden by the per-item "View" filter.
  const childRows = viewItem
    ? []
    : childItems.map((item) => ({ kind: "childItem" as const, id: `child-${item.id}`, data: { name: item.name } }));
  return [simpleHeader, ...rows, ...childRows];
}

type FinishScheduleConfigForm = {
  id: string;
  sortOrder: number | null | undefined;
  shown: boolean | null | undefined;
  type: FinishScheduleColumnType;
  sourceAttribute: string | null | undefined;
  coreColumnName: string | null | undefined;
  label: string | null | undefined;
  useInVisualMode: boolean | null | undefined;
};

type CostCodeForm = Pick<
  SaveCostCodeInput,
  | "id"
  | "interiorFinishScheduleLabel"
  | "interiorFinishScheduleDescription"
  | "exteriorFinishScheduleLabel"
  | "exteriorFinishScheduleDescription"
> & { finishScheduleConfigs: FinishScheduleConfigForm[] };

const finishScheduleConfigConfig: ObjectConfig<FinishScheduleConfigForm> = {
  id: { type: "value" },
  sortOrder: { type: "value" },
  shown: { type: "value" },
  type: { type: "value" },
  sourceAttribute: { type: "value" },
  coreColumnName: { type: "value" },
  label: { type: "value" },
  useInVisualMode: { type: "value" },
};

const formConfig: ObjectConfig<CostCodeForm> = {
  id: { type: "value" },
  interiorFinishScheduleLabel: { type: "value" },
  interiorFinishScheduleDescription: { type: "value" },
  exteriorFinishScheduleLabel: { type: "value" },
  exteriorFinishScheduleDescription: { type: "value" },
  finishScheduleConfigs: { type: "list", config: finishScheduleConfigConfig },
};

function mapToForm(cc: FinishScheduleCostCodeDetailFragment, location: DesignPackageLocation): CostCodeForm {
  const isExterior = location === DesignPackageLocation.Exterior;
  const configs = isExterior ? cc.exteriorFinishScheduleConfigs : cc.interiorFinishScheduleConfigs;
  return {
    id: cc.id,
    // Pre-fill the section label with the cost code's name when no custom label is set. Designs call
    // for pre-populating (not a placeholder) so the user can edit the real text directly. This mirrors
    // the backend's own fallback, and because it's set as the form *baseline*, `changedValue` won't
    // persist the name back as a custom label unless the user actually edits it.
    interiorFinishScheduleLabel: isExterior ? undefined : (cc.interiorFinishScheduleLabel ?? cc.name),
    interiorFinishScheduleDescription: isExterior ? undefined : cc.interiorFinishScheduleDescription,
    exteriorFinishScheduleLabel: isExterior ? (cc.exteriorFinishScheduleLabel ?? cc.name) : undefined,
    exteriorFinishScheduleDescription: isExterior ? cc.exteriorFinishScheduleDescription : undefined,
    finishScheduleConfigs: configs.map((c) => ({
      id: c.id,
      sortOrder: c.sortOrder,
      shown: c.shown,
      type: c.type.code,
      sourceAttribute: c.sourceAttribute,
      coreColumnName: c.coreColumn?.name,
      label: c.label ?? c.coreColumn?.name ?? c.sourceAttribute,
      useInVisualMode: c.useInVisualMode,
    })),
  };
}
